import { useEffect, useMemo, useRef, useState } from 'react'
import { ResizableBox, ResizeHandle } from 'react-resizable'
import cx from 'classnames'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

import { EmbedFormat } from 'types'
import { useAuth } from 'hooks/UseAuth'
import { useWorkspace } from 'hooks/useWorkspace'

import Styles from './Iframe.module.scss'
import { resizeHandleToCursor } from 'utils/resizable'
import { Spin } from 'common/Spin/Spin'
import { useReactNodeView } from '../ReactNodeView/ReactNodeViewContext'
import { NodeSelection } from 'prosemirror-state'
import { FormatMenu } from './FomatMenu'
import { Card } from './Card'
import { SmartLink } from './SmartLink'
import { renderFallbackThumbnail } from './utils'

const MIN_WIDTH = 200
const MIN_HEIGHT = 200
const MIN_VIDEO_WIDTH = 355.55555
const MIN_VIDEO_HEIGHT = 200
const IFRAME_DEFAULT_WIDTH = 680
const IFRAME_DEFAULT_HEIGHT = 382.5
const ASPECT_RATIO = 1.7777777

export const Iframe = () => {
  const context = useReactNodeView()
  const { node, view } = context
  const auth = useAuth()
  const workspaceId = auth && auth.company_id
  const workspace = useWorkspace(workspaceId)
  const [boxHeight, setBoxHeight] = useState(300)
  const [boxWidth, setBoxWidth] = useState(600)
  const [loading, setLoading] = useState(true)
  const [resizeHandle, setResizeHandle] = useState<null | ResizeHandle>(null)
  const [resizeListener, setResizeListener] = useState<EventListener>()
  const containerRef = useRef<HTMLDivElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const getPos = context.getPos as () => number
  const isIframeSelected = view?.state.selection instanceof NodeSelection && view?.state.selection.from === getPos()
  const readOnly = false
  const inCommentMode = false
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const embedFormat = node!.attrs.format as EmbedFormat
  const { isMobile } = useResponsiveDesign()
  const isVideo = useMemo(() => {
    return (
      node?.attrs.provider.includes('youtube') ||
      node?.attrs.provider.includes('vimeo') ||
      (node?.attrs.provider === 'generic' && node?.attrs.url.startsWith('https://www.loom.com/share/'))
    )
  }, [node])
  useEffect(() => {
    if (embedFormat !== 'embed') return

    const determineElementDimensions = () => {
      let height = 0
      let width = 0

      if (containerRef.current && containerRef.current.parentElement) {
        if (iframeRef.current) {
          const iframeBody = iframeRef.current.contentDocument?.body

          if (iframeBody?.firstChild) {
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            ;(iframeBody!.firstChild as HTMLElement).style.maxWidth = 'none'
          }

          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          width = ((node!.attrs.width as number) <= 1 ? (node!.attrs.width as number) : 1) * IFRAME_DEFAULT_WIDTH

          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          height = ((node!.attrs.height as number) <= 10 ? (node!.attrs.height as number) : 1) * IFRAME_DEFAULT_HEIGHT

          setBoxHeight(height)
          setBoxWidth(width)
        }
      }
    }

    afterImageLoad(() => determineElementDimensions())
    setResizeListener(() => determineElementDimensions())
  }, [readOnly, node, node?.attrs.width, node?.attrs.height, embedFormat])

  useEffect(() => {
    window.addEventListener('resize', resizeListener as EventListener)

    return () => {
      window.removeEventListener('resize', resizeListener as EventListener)
    }
  }, [resizeListener])

  const afterImageLoad = (callback: () => void) => {
    if (iframeRef.current) {
      iframeRef.current.onload = () => {
        setLoading(false)
        callback()
      }
    }
  }

  const getResizeHandle = (position: string) => {
    return (
      <span
        className={cx(Styles.resizeHandle, Styles[`resizeHandle-${position}`], {
          [Styles.hide]: !isIframeSelected,
        })}
        data-test-id={`embed-resize-handle-${position}`}
      />
    )
  }

  const renderIframe = () => {
    return (
      <iframe
        ref={iframeRef}
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        src={(node!.attrs.src as string)?.length ? (node!.attrs.src as string) : undefined}
        srcDoc={`<style>body{margin:0px;}</style>${
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          (node!.attrs.srcdoc as string)?.length ? (node!.attrs.srcdoc as string) : ''
        }`}
        title="Embed"
        className={cx({ [Styles.hide]: loading })}
        style={readOnly || inCommentMode ? { height: boxHeight, width: boxWidth } : {}}
        /** TODO(@gbarriga): We should consider sandboxing all iframes moving forward, as this is a not insignificant security risk.
         * Need to explore best practices in a seperate ticket.
         */
        // sandbox="allow-scripts"
      />
    )
  }

  const renderFormatMenu = () => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return <FormatMenu attrs={node!.attrs} />
  }

  const renderEmbedFormat = () => {
    let embedFrameNode = (
      <>
        {(!isIframeSelected || resizeHandle) && (
          <div className={cx(Styles.containerOverlay)} data-test-id="embed-overlay"></div>
        )}
        {loading && <Spin className={Styles.spinner} />}
        {renderIframe()}
      </>
    )

    if (!isMobile) {
      embedFrameNode = (
        <ResizableBox
          className={Styles.resizeContainer}
          height={isVideo ? boxWidth / ASPECT_RATIO : boxHeight}
          width={boxWidth}
          lockAspectRatio={isVideo}
          onResizeStop={(_event, { size: { width, height } }) => {
            const {
              dispatch,
              state: { tr },
            } = // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              view!
            setResizeHandle(null)

            const newAttrs = {
              width: width / IFRAME_DEFAULT_WIDTH,
              height: (isVideo ? width / ASPECT_RATIO : height) / IFRAME_DEFAULT_HEIGHT,
            }
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            dispatch(tr.setNodeMarkup(getPos(), undefined, { ...node!.attrs, ...newAttrs }))
          }}
          onResizeStart={(_event, { handle }) => setResizeHandle(handle)}
          minConstraints={isVideo ? [MIN_VIDEO_WIDTH, MIN_VIDEO_HEIGHT] : [MIN_WIDTH, MIN_HEIGHT]}
          resizeHandles={[...(isVideo ? [] : (['n', 'e', 's', 'w'] as const)), 'nw', 'ne', 'se', 'sw']}
          handle={getResizeHandle}
        >
          {resizeHandle && (
            <style>{`
                  body {
                    cursor: ${resizeHandleToCursor(resizeHandle)};
                  }
                `}</style>
          )}
          {embedFrameNode}
        </ResizableBox>
      )
    }

    return (
      <>
        {(readOnly || inCommentMode) && (
          <div ref={containerRef} className={Styles.iframeContainer}>
            {loading && <Spin className={Styles.spinner} />}
            {renderIframe()}
          </div>
        )}

        {!readOnly && !inCommentMode && (
          <div
            ref={containerRef}
            className={cx(Styles.iframeContainer, { [Styles.iframeContainerSelected]: isIframeSelected })}
            data-test-id="embed-position-container"
          >
            {isIframeSelected && <>{renderFormatMenu()}</>}
            {embedFrameNode}
          </div>
        )}
      </>
    )
  }

  const renderUrlFormat = () => {
    return (
      <>
        <div className={cx(Styles.urlContainer, { [Styles.selected]: isIframeSelected })}>
          {isIframeSelected && !readOnly && !inCommentMode && <>{renderFormatMenu()}</>}
          {!isIframeSelected && !readOnly && !inCommentMode && <div className={cx(Styles.containerOverlay)}></div>}
          {/* TODO: Fix this the next time the file is edited. */}
          {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
          <a href={node!.attrs.url as string} target="_blank" rel="noreferrer noopener">
            {/* TODO: Fix this the next time the file is edited. */}
            {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
            {renderFallbackThumbnail(node!.attrs.provider as string)}
            {/* TODO: Fix this the next time the file is edited. */}
            {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
            {node!.attrs.url}
          </a>
        </div>
      </>
    )
  }

  const renderFormattedEmbed = () => {
    switch (embedFormat) {
      case 'url':
        return renderUrlFormat()
      case 'card':
        return (
          <Card
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            attrs={node!.attrs}
            selected={isIframeSelected}
            readOnly={readOnly || inCommentMode}
            workspace={workspace}
          />
        )
      case 'smartLink':
        return (
          <SmartLink
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            attrs={node!.attrs}
            selected={isIframeSelected}
            readOnly={readOnly || inCommentMode}
            workspace={workspace}
          />
        )
      case 'embed':
      default:
        return renderEmbedFormat()
    }
  }

  return renderFormattedEmbed()
}
